// 格式化日期时间
export const formatDateTime = (date, format = 'datetime') => {
  if (!date) return '未知';
  
  const time = new Date(date);
  
  // 无效日期
  if (isNaN(time.getTime())) return '无效日期';
  
  const yyyy = time.getFullYear();
  const MM = String(time.getMonth() + 1).padStart(2, '0');
  const dd = String(time.getDate()).padStart(2, '0');
  const hh = String(time.getHours()).padStart(2, '0');
  const mm = String(time.getMinutes()).padStart(2, '0');
  const ss = String(time.getSeconds()).padStart(2, '0');
  
  switch (format) {
    case 'date':
      return `${yyyy}-${MM}-${dd}`;
    case 'time':
      return `${hh}:${mm}:${ss}`;
    case 'datetime':
      return `${yyyy}-${MM}-${dd} ${hh}:${mm}`;
    case 'full':
      return `${yyyy}-${MM}-${dd} ${hh}:${mm}:${ss}`;
    default:
      return `${yyyy}-${MM}-${dd} ${hh}:${mm}`;
  }
};

// 格式化日期
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

// 格式化时间
export const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 格式化相对时间
 * @param {string|Date} date - 日期字符串或日期对象
 * @param {boolean} withPrefix - 是否包含前缀
 * @returns {string} 相对时间字符串
 */
export const formatRelativeTime = (date, withPrefix = true) => {
  if (!date) return '未知';
  
  const now = new Date();
  const time = new Date(date);
  const diff = Math.floor((now - time) / 1000); // 时间差（秒）
  
  // 无效日期
  if (isNaN(time.getTime())) return '无效日期';
  
  const prefix = withPrefix ? '约' : '';
  
  if (diff < 60) {
    return '刚刚';
  } else if (diff < 3600) {
    const minutes = Math.floor(diff / 60);
    return `${prefix}${minutes}分钟前`;
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${prefix}${hours}小时前`;
  } else if (diff < 2592000) {
    const days = Math.floor(diff / 86400);
    return `${prefix}${days}天前`;
  } else if (diff < 31536000) {
    const months = Math.floor(diff / 2592000);
    return `${prefix}${months}个月前`;
  } else {
    const years = Math.floor(diff / 31536000);
    return `${prefix}${years}年前`;
  }
};

// 格式化重要性等级
export const formatImportance = (importance) => {
  switch (importance) {
    case 'high':
      return {
        label: '高',
        color: '#dc3545',
        className: 'text-danger'
      };
    case 'medium':
      return {
        label: '中',
        color: '#fd7e14',
        className: 'text-warning'
      };
    case 'low':
      return {
        label: '低',
        color: '#20c997',
        className: 'text-success'
      };
    default:
      return {
        label: '未知',
        color: '#6c757d',
        className: 'text-secondary'
      };
  }
};

// 格式化变更类型
export const formatChangeType = (type) => {
  switch (type) {
    case 'content':
      return {
        label: '内容变更',
        color: '#0d6efd',
        className: 'text-primary',
        icon: 'file-alt'
      };
    case 'layout':
      return {
        label: '布局变更',
        color: '#6f42c1',
        className: 'text-purple',
        icon: 'columns'
      };
    case 'visual':
      return {
        label: '视觉变更',
        color: '#e83e8c',
        className: 'text-pink',
        icon: 'image'
      };
    case 'text':
      return {
        label: '文本变更',
        color: '#20c997',
        className: 'text-teal',
        icon: 'font'
      };
    case 'meta':
      return {
        label: '元数据变更',
        color: '#fd7e14',
        className: 'text-orange',
        icon: 'tags'
      };
    case 'error':
      return {
        label: '错误',
        color: '#dc3545',
        className: 'text-danger',
        icon: 'exclamation-circle'
      };
    default:
      return {
        label: '未知变更',
        color: '#6c757d',
        className: 'text-secondary',
        icon: 'question-circle'
      };
  }
};

// 截断文本
export const truncateText = (text, length = 50) => {
  if (!text) return '';
  if (text.length <= length) return text;
  
  return text.substring(0, length) + '...';
};

// 格式化URL (移除协议和末尾斜杠)
export const formatUrl = (url, length = 30) => {
  if (!url) return '';
  
  // 移除协议前缀
  let formattedUrl = url.replace(/^https?:\/\//, '');
  
  // 移除末尾的斜杠
  formattedUrl = formattedUrl.replace(/\/$/, '');
  
  // 如果超出长度限制，截断并添加省略号
  if (formattedUrl.length > length) {
    formattedUrl = formattedUrl.substring(0, length) + '...';
  }
  
  return formattedUrl;
};

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @param {number} decimals - 小数位数
 * @returns {string} 格式化后的文件大小
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * 格式化监控频率
 * @param {string} frequency - 监控频率代码
 * @returns {string} 格式化后的监控频率描述
 */
export const formatFrequency = (frequency) => {
  switch (frequency) {
    case 'realtime':
      return '实时';
    case 'minutes_5':
      return '每5分钟';
    case 'minutes_15':
      return '每15分钟';
    case 'minutes_30':
      return '每30分钟';
    case 'hourly':
      return '每小时';
    case 'hours_3':
      return '每3小时';
    case 'hours_6':
      return '每6小时';
    case 'hours_12':
      return '每12小时';
    case 'daily':
      return '每天';
    case 'days_3':
      return '每3天';
    case 'weekly':
      return '每周';
    default:
      return frequency || '未知';
  }
};

/**
 * 格式化站点状态
 * @param {string} status - 站点状态代码
 * @returns {object} 格式化后的状态信息 { label, color, className, icon }
 */
export const formatSiteStatus = (status) => {
  switch (status) {
    case 'normal':
      return {
        label: '正常',
        color: '#28a745',
        className: 'text-success',
        icon: 'check-circle'
      };
    case 'warning':
      return {
        label: '警告',
        color: '#ffc107',
        className: 'text-warning',
        icon: 'exclamation-triangle'
      };
    case 'error':
      return {
        label: '错误',
        color: '#dc3545',
        className: 'text-danger',
        icon: 'times-circle'
      };
    case 'pending':
      return {
        label: '待检查',
        color: '#6c757d',
        className: 'text-secondary',
        icon: 'clock'
      };
    case 'inactive':
      return {
        label: '未激活',
        color: '#6c757d',
        className: 'text-muted',
        icon: 'pause-circle'
      };
    default:
      return {
        label: '未知',
        color: '#6c757d',
        className: 'text-secondary',
        icon: 'question-circle'
      };
  }
}; 